<template>
    <datepicker
        :format="customDateFormater"
        :monday-first="isUserLangFr"
        ref="datePicker"
        :highlighted="highlighted"
        :language="datePickerLanguage"
        v-model="date"
        @selected="$emit('selected', $event)"
        v-bind="forwardedBindings">
        <template #afterDateInput>
            <feather
                v-if="showClearButton_ && date !== null"
                class="me-1 text-danger pointer"
                style="position: absolute; top: 10px; right: 0"
                type="x"
                @click="date = null" />
        </template>
    </datepicker>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { fr, en } from "vuejs-datepicker/dist/locale";
import LangsEnum from "../../mixins/enums/LangsEnum";
import { DateTime } from "luxon";

export default {
    inheritAttrs: false,
    emits: ["input", "update:modelValue", "selected"],
    props: {
        modelValue: {
            type: DateTime,
            default: undefined,
        },
        value: {
            type: DateTime,
            default: undefined,
        },
        canBeEmpty: {
            type: Boolean,
            default: false,
        },
        showClearButton: {
            type: Boolean,
            default: false,
        },
        timezone: {
            type: String,
            default: undefined,
        },
        format: {
            default: () => DateTime.DATE_FULL,
        },
    },
    mixins: [LangsEnum],
    methods: {
        getDatePicker() {
            return this.$refs.datePicker;
        },
        customDateFormater(date) {
            const dateTime = DateTime.fromJSDate(date);
            if (typeof this.format === "string") {
                return dateTime.toFormat(this.format);
            }
            if (typeof this.format === "object") {
                return dateTime.toLocaleString(this.format);
            }
            if (typeof this.format === "function") {
                return this.format(dateTime);
            }
            console.error(`Invalid format given in DatePicker::customDateFormater: "${this.format}"`);
            return dateTime.toLocaleString(DateTime.DATE_FULL);
        },
    },
    computed: {
        forwardedBindings() {
            return {
                ...this.forwardedAttrs,
                ...this.forwardedListeners,
            };
        },
        forwardedAttrs() {
            return Object.fromEntries(Object.entries(this.$attrs).filter(([key]) => !key.startsWith("on")));
        },
        forwardedListeners() {
            return Object.fromEntries(
                Object.entries(this.$attrs).filter(([key, value]) => {
                    if (!key.startsWith("on")) {
                        return false;
                    }

                    if (key === "onInput" || key === "onUpdate:modelValue") {
                        return false;
                    }

                    return typeof value === "function" || Array.isArray(value);
                })
            );
        },
        highlighted() {
            return {
                dates: [this.getDateTime().toJSDateCustom()],
            };
        },
        showClearButton_() {
            return this.showClearButton && this.canBeEmpty;
        },
        date: {
            get() {
                const currentValue = this.modelValue instanceof DateTime ? this.modelValue : this.value;

                if (currentValue instanceof DateTime) {
                    return currentValue.toJSDateCustom();
                }
                if (this.canBeEmpty) {
                    return null;
                }
                return this.getJSDateFromDateTime();
            },
            set(date) {
                const nextValue = date === null ? (this.canBeEmpty ? null : this.getDateTime()) : this.getDateTime(date, false, this.timezone);

                if (date === null) {
                    this.$emit("input", nextValue);
                    this.$emit("update:modelValue", nextValue);
                    return;
                }

                this.$emit("input", nextValue);
                this.$emit("update:modelValue", nextValue);
            },
        },
        datePickerLanguage() {
            if (this.isUserLangFr) {
                return fr;
            }
            return en;
        },
    },
    components: {
        Datepicker,
    },
    created() {
        // If can not be empty, fire an event on creation to fill value
        const currentValue = this.modelValue instanceof DateTime ? this.modelValue : this.value;

        if (!this.canBeEmpty && (typeof currentValue === "undefined" || currentValue === null)) {
            const nextValue = this.getDateTime(this.date, false, this.timezone);

            this.$emit("input", nextValue);
            this.$emit("update:modelValue", nextValue);
        }
    },
};
</script>

<style>
.vdp-datepicker .vdp-datepicker__calendar span.cell.today:not(.highlighted):not(.selected) {
    border: 1px solid;
    border-color: transparent !important;
    background-color: transparent;
}

.vdp-datepicker .vdp-datepicker__calendar .cell.highlighted {
    border: 1px solid;
    border-color: #f3f3f3;
    background-color: #f3f3f3;
}
</style>
